import { brandConfig } from '../../config/brands';
import type { ProductImageSlot } from './shared/productImageSlots';
import type { ManualImage } from './shared/imageSlotReorder';

/**
 * マニュアル登録商品詳細ページのアダプタ (純関数)。
 * products.main_image_url / main_image_name / additional_images から共通コンポーネント用の
 * 枠モデル (ProductImageSlot) を組み立てる。
 * 計画: docs/requirements/product-detail-page-consolidation-plan.md 4.3
 */

/** ページ内で画像枠を指す形 (既存ハンドラの引数) */
export type ManualSlotRef = { type: 'main' } | { type: 'sub'; index: number };

/** 枠キー: main / sub-0.. */
export function manualSlotKey(ref: ManualSlotRef): string {
  return ref.type === 'main' ? 'main' : `sub-${ref.index}`;
}

export function parseManualSlotKey(key: string): ManualSlotRef | null {
  if (key === 'main') return { type: 'main' };
  const m = /^sub-(\d+)$/.exec(key);
  if (!m) return null;
  const index = Number(m[1]);
  return index < MANUAL_SUB_SLOT_MAX ? { type: 'sub', index } : null;
}

export type ManualProductImages = {
  title?: string | null;
  main_image_url?: string | null;
  main_image_name?: string | null;
  main_image_original_path?: string | null;
  additional_images?: ManualImage[] | null;
};

/** 枠にベクター原本 (SVG) があるか (meta.original_path)。SVG バッジと原本ダウンロードの表示に使う */
export function slotHasVectorOriginal(slot: Pick<ProductImageSlot, 'url' | 'meta'>): boolean {
  return !!slot.url && typeof slot.meta?.original_path === 'string' && (slot.meta.original_path as string).endsWith('.svg');
}

/**
 * 追加画像の上限 (利用者指示 2026-10-30: 枚数を利用者が任意で増やせるようにした)。
 * BE の枠番号の検証 (backend/routes/products.py の MANUAL_ADDITIONAL_IMAGES_MAX) と同じ値
 */
export const MANUAL_SUB_SLOT_MAX = 30;

/**
 * 追加画像の枠数: 入っている画像 (末尾の空きは数えない) + 追加用の空き枠 1 つ。上限に達したら空き枠は出さない。
 * メイン画像も追加画像も無いときは 0 (メイン画像の枠だけを出し、メインから入れてもらう・利用者指示 2026-10-30)
 */
export function manualSubSlotCount(additional: ManualImage[] | null | undefined, hasMain: boolean): number {
  let filled = 0;
  (additional ?? []).forEach((img, i) => { if (img?.url?.trim()) filled = i + 1; });
  if (!hasMain && filled === 0) return 0;
  return Math.min(filled + 1, MANUAL_SUB_SLOT_MAX);
}

/**
 * [main, sub-0..sub-(n-1)] の枠。n は manualSubSlotCount (画像の数 + 追加用の空き枠 1 つ・画像が 1 枚も無ければ 0)。
 * additional_images の url が空白 ({url: ''} の穴) は空き枠。上限より後ろの要素は枠に出さず、保存時に保持する
 * (buildManualReorderPayload の extra)。
 * name はトップレベルの name に、width / height / original_path (ベクター原本のパス) は meta に写す。
 * 原本のパスは枠の種類に依らず meta に統一し、並べ替えでメイン ⇄ 追加を動いても一緒に運ぶ (§3.7)。
 */
export function buildManualSlots(product: ManualProductImages | null | undefined): ProductImageSlot[] {
  const mainUrl = product?.main_image_url?.trim() || null;
  const mainMeta: Record<string, unknown> = {};
  if (mainUrl && product?.main_image_original_path) mainMeta.original_path = product.main_image_original_path;
  const mainName = mainUrl ? product?.main_image_name || null : null;
  const slots: ProductImageSlot[] = [{
    key: 'main',
    label: 'メイン画像',
    // 名前があれば見出しに出す (サブ画像と同じく既定で見える・利用者指示 2026-11-05)。メインであることは残す
    headerLabel: mainName ? `メイン画像: ${mainName}` : undefined,
    imageAlt: product?.title || 'Product',
    isMain: true,
    url: mainUrl,
    name: mainName,
    meta: mainMeta,
    formatBadge: slotHasVectorOriginal({ url: mainUrl, meta: mainMeta }) ? 'SVG' : undefined,
    locked: false,
  }];
  const subCount = manualSubSlotCount(product?.additional_images, !!mainUrl);
  for (let i = 0; i < subCount; i++) {
    const img = product?.additional_images?.[i];
    const url = img?.url?.trim() || null;
    const name = url ? img?.name || null : null;
    const meta: Record<string, unknown> = {};
    if (img?.width != null) meta.width = img.width;
    if (img?.height != null) meta.height = img.height;
    if (url && img?.original_path) meta.original_path = img.original_path;
    const label = `画像 ${i + 1}`;
    slots.push({
      key: `sub-${i}`,
      label,
      // マニュアル商品は画像番号に意味がないため、名前があれば見出しに名前を表示する
      headerLabel: name || label,
      isMain: false,
      url,
      name,
      meta,
      formatBadge: slotHasVectorOriginal({ url, meta }) ? 'SVG' : undefined,
      locked: false,
    });
  }
  return slots;
}

/** 差替・削除ボタンのツールチップ (現行文言のまま。追加画像の削除は「画像N」と詰める) */
export function manualActionTooltips(slot: ProductImageSlot): { replace: string; delete: string } {
  if (slot.isMain) {
    return { replace: 'マニュアル登録のメイン画像を差替', delete: 'マニュアル登録からメイン画像を削除' };
  }
  const ref = parseManualSlotKey(slot.key);
  const n = ref?.type === 'sub' ? ref.index + 1 : 0;
  return { replace: `マニュアル登録の画像 ${n}を差替`, delete: `マニュアル登録から画像${n}を削除` };
}

/**
 * 並べ替えを開始できない理由 (null = 開始できる)。現行の canStartReorder と同じ条件:
 * 編集モード、保存中、画像なし。
 */
export function manualReorderDisabledReason(args: {
  slots: ProductImageSlot[];
  isEditing: boolean;
  /** updateProduct.isPending (保存中) */
  isSaving: boolean;
}): string | null {
  const { slots, isEditing, isSaving } = args;
  if (isEditing) return `${brandConfig.labels.productShort}情報の編集中は並べ替えできません`;
  if (isSaving) return '保存中は並べ替えできません';
  if (!slots.some((slot) => !!slot.url)) return '並べ替えできる画像がありません';
  return null;
}
